/**
 * D-1024-TECHHELPER (part 1) — M-Lab's ndt7 speed test, the pure parts: the locate answer, the server's
 * measurements and the Mbps math. The browser client that drives the sockets is src/features/tech/net/ndt7.ts.
 * Protocol: https://github.com/m-lab/ndt-server/blob/main/spec/ndt7-protocol.md
 *
 * Only test traffic goes to M-Lab: nothing about a tenant, a ticket or a person is ever sent.
 */

export const NDT7_LOCATE_URL = 'https://locate.measurementlab.net/v2/nearest/ndt/ndt7';
export const NDT7_SUBPROTOCOL = 'net.measurementlab.ndt.v7';
/** Servers are only ever M-Lab's own: wss:// on measurement-lab.org. */
export const NDT7_HOST = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.measurement-lab\.org$/i;

export interface Ndt7Target {
  download: string;
  upload: string;
  /** "lga05" — the site, for the small print and the log; null if not given. */
  site: string | null;
}

function safeWss(u: unknown): string | null {
  if (typeof u !== 'string') return null;
  try {
    const url = new URL(u);
    return url.protocol === 'wss:' && NDT7_HOST.test(url.hostname) && !url.username && !url.password ? url.toString() : null;
  } catch {
    return null;
  }
}

/** The first locate result that has both a download and an upload address on M-Lab's own hosts. */
export function parseLocate(json: unknown): Ndt7Target | null {
  const results = (json as { results?: unknown })?.results;
  if (!Array.isArray(results)) return null;
  for (const r of results) {
    const urls = (r as { urls?: Record<string, unknown> })?.urls ?? {};
    const download = safeWss(urls['wss:///ndt/v7/download']);
    const upload = safeWss(urls['wss:///ndt/v7/upload']);
    if (download && upload) {
      const machine = (r as { machine?: unknown }).machine;
      const site = typeof machine === 'string' ? (/^mlab\d-([a-z]{3}\d{2})\b/i.exec(machine)?.[1] ?? null) : null;
      return { download, upload, site };
    }
  }
  return null;
}

/** Megabits a second for `bytes` moved in `ms` (one decimal). 0 for no time or no bytes. */
export function mbps(bytes: number, ms: number): number {
  if (!(bytes > 0) || !(ms > 0)) return 0;
  return Math.round(((bytes * 8) / (ms * 1000)) * 10) / 10;
}

export interface ServerMeasurement {
  /** TCPInfo.MinRTT, milliseconds. */
  minRttMs: number | null;
  /** TCPInfo.BytesReceived (upload: what the server got). */
  bytesReceived: number | null;
  /** TCPInfo.ElapsedTime, milliseconds. */
  elapsedMs: number | null;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null);

/** One text message from the server. Unreadable or hostile input is null, never an exception. */
export function parseServerMeasurement(data: unknown): ServerMeasurement | null {
  if (typeof data !== 'string' || data.length > 20_000) return null;
  try {
    const m = JSON.parse(data) as { TCPInfo?: Record<string, unknown>; BBRInfo?: Record<string, unknown> } | null;
    if (!m || typeof m !== 'object') return null;
    const rtt = num(m.TCPInfo?.MinRTT) ?? num(m.BBRInfo?.MinRTT);
    const elapsed = num(m.TCPInfo?.ElapsedTime);
    return { minRttMs: rtt === null ? null : Math.round((rtt / 1000) * 10) / 10, bytesReceived: num(m.TCPInfo?.BytesReceived), elapsedMs: elapsed === null ? null : elapsed / 1000 };
  } catch {
    return null;
  }
}

/** Upload Mbps from the server's own last measurement (what it received, over its elapsed time). */
export function uploadMbpsFromServer(m: ServerMeasurement | null): number | null {
  return m && m.bytesReceived && m.elapsedMs ? mbps(m.bytesReceived, m.elapsedMs) : null;
}
